import db, { createRef } from '../../../../db'
import { createRef as createRefConn } from '../../../../db/connection'
import { converter2 } from '../../../../db/converter'
import { Collaboration, CollabsStatus, Room } from '../../../models/Chat'
import Message from '../../../models/Message'
import { UserCompanies } from '../../../models/User'
import * as lc from '../../localstorage'
import * as userService from '../../../../db/serviceUser'
import { removeFromNotificationsByCollabID } from '../../notify/Notification/helper'
import { ICollaborationRepository } from './ICollaborationRepository'
import { arrayUnion, Timestamp, DocumentReference, Query } from 'firebase/firestore'
import { ChatType } from '../feature'

/**
 * Collaboration Repository Implementation
 * Contains all Firestore queries for the collaborations collection
 * Migrated from src/db/serviceCollaborations.tsx
 */
class CollaborationRepositoryImpl implements ICollaborationRepository {
  private static instance: CollaborationRepositoryImpl

  static getInstance(): CollaborationRepositoryImpl {
    if (!this.instance) {
      this.instance = new CollaborationRepositoryImpl()
    }
    return this.instance
  }

  // ============================================================================
  // QUERY METHODS
  // ============================================================================

  /**
   * Get a single collaboration by ID with user enrichment
   * Migrated from: serviceCollaborations.getByID
   */
  async getById(collabId: string): Promise<Collaboration | null> {
    try {
      const collab = await db
        .collection('collaborations')
        .doc(collabId)
        .withConverter(converter2<Collaboration>())
        .get()

      if (!collab.exists) {
        return null
      }

      // Enrich toUser references
      let toUserData: any[] = []
      const collabData = collab.data()
      if (collabData?.toUser) {
        const userPromises = collabData.toUser.map(async (item: any) => {
          const user = await userService.getCachedUserByID(item.id)
          if (user) {
            toUserData.push(user)
          }
        })
        await Promise.all(userPromises)
      }

      return {
        ...collabData,
        id: collab.id,
        toUserData,
      } as Collaboration
    } catch (error) {
      console.error('Error in getById:', error)
      return null
    }
  }

  /**
   * Get collaborations by customer reference
   * Migrated from: serviceCollaborations.getCollborationbyCustomerRef
   */
  async getByCustomerRef(customerId: string): Promise<Collaboration[]> {
    try {
      const querySnapshot = await db
        .collection('collaborations')
        .where('customer', '==', createRef('customers', customerId))
        .get()

      return querySnapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      })) as Collaboration[]
    } catch (error) {
      console.error('Error in getByCustomerRef:', error)
      return []
    }
  }

  /**
   * Get collaborations for "My Chat" - handled by current user
   * Migrated from: serviceCollaborations.fetchCollaborationsByUser
   */
  async getByUser(uid: string, clientId: string): Promise<Collaboration[]> {
    const userRef = createRefConn('users', uid)
    const company = lc.getItemLC(lc.LCName.CompanyList)

    let query = db
      .collection('collaborations')
      .withConverter(converter2<Collaboration>())
      .where('client', '==', createRef('clients', clientId))
      .where('toUser', 'array-contains', userRef)
      .where('status', '==', 'handled')

    const hasAllCompanyAccess = company?.find(
      (accessCompany: any) => accessCompany.id === '*'
    )

    if (!hasAllCompanyAccess && company?.length > 0) {
      const companyAccessQuery = company.map((c: any) =>
        createRef('company', c.id)
      )
      query
        .where('company', 'in', companyAccessQuery)
        .where('client', '==', createRef('clients', clientId))
        .where('isActive', '==', true)
        .where('status', '==', CollabsStatus.queue)
    }

    const snapshot = await query.orderBy('lastInteractionAt', 'desc').get()

    const collaborations = await Promise.all(
      snapshot.docs.map(async (doc) => {
        const roomsDoc = await doc.ref
          .collection('rooms')
          .get()
          .then(async (querySnapshot) => {
            return Promise.all(
              querySnapshot.docs.map(async (roomDoc) => {
                if (roomDoc.exists) {
                  const accountRef = roomDoc.data()?.account?.id
                  let accountsModel = null as any

                  if (accountRef) {
                    const accountDoc = await db
                      .collection('account')
                      .doc(accountRef)
                      .get()
                    if (accountDoc.exists) {
                      accountsModel = accountDoc.data()
                    }
                  }

                  let temp: any[] = []
                  if (roomDoc.data()?.toUser) {
                    const userPromises =
                      roomDoc.data()?.toUser?.map(async (item: any) => {
                        const user = await userService.getUserByID(item.id)
                        if (user) {
                          temp?.push(user)
                        }
                      }) || []
                    await Promise.all(userPromises)
                  }

                  return {
                    ...roomDoc.data(),
                    id: roomDoc.id,
                    accountsModel,
                    toUserModel: temp,
                  }
                }
                return undefined
              })
            )
          })

        return { ...doc.data(), id: doc.id, roomsModel: roomsDoc }
      })
    )

    // Note: roomsModel is an enrichment property not in base Collaboration type
    // Service layer will handle proper typing
    return collaborations as any[]
  }

  /**
   * Get collaborations for "All Chat" - queue status accessible by company
   * Migrated from: serviceCollaborations.fetchCollaborationsByAllCompanyUser
   */
  async getByAllCompanyUser(
    companies: Array<DocumentReference>
  ): Promise<Collaboration[]> {
    try {
      const currentClient = lc.getItemLC(lc.LCName.Client)
      const clientId = currentClient.id
      const collaborationsQuery = db
        .collection('collaborations')
        .withConverter(converter2<Collaboration>())
        .where('client', '==', createRef('clients', clientId))
        .where('isActive', '==', true)
        .where('status', '==', CollabsStatus.queue)

      const hasAllCompanyAccess = companies?.find(
        (accessCompany) => accessCompany.id === '*'
      )

      if (!hasAllCompanyAccess && companies?.length > 0) {
        const companyAccessQuery = companies.map((company) =>
          createRef('company', company.id)
        )
        collaborationsQuery.where('company', 'in', companyAccessQuery)
      }

      const collaborations = await collaborationsQuery.get().then((snapshot) => {
        return snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }))
      })

      const useruid = lc.getItemLC(lc.LCName.UserID)
      const userref = createRef('users', useruid)

      const filteredCollaborations = await Promise.all(
        collaborations.map(async (collaboration) => {
          const roomsQuery = await db
            .collection(`collaborations/${collaboration?.id}/rooms`)
            .where('status', '!=', 'queue')
            .get()

          const rooms = roomsQuery.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          })) as Array<{ id: string; toUser?: Array<any> }>

          const validRooms = rooms.filter((room) => {
            return (
              room.toUser &&
              !room.toUser.some(
                (userRef: any) => userRef.path === userref.path
              )
            )
          })

          return validRooms.length > 0 ? collaboration : null
        })
      )

      return filteredCollaborations.filter(
        (collab) => collab !== null
      ) as Collaboration[]
    } catch (error) {
      console.error('Error in getByAllCompanyUser:', error)
      throw error
    }
  }

  /**
   * Get friend/internal collaborations for user
   * Migrated from: serviceCollaborations.fetchFriendCollaborationsByUser
   */
  async getFriendCollaborations(uid: string): Promise<Collaboration[]> {
    const userRef = createRefConn('users', uid)

    const snapshot = await db
      .collection('collaborations')
      .withConverter(converter2<Collaboration>())
      .where('isActive', '==', true)
      .where('joinedUsers', 'array-contains', userRef)
      .orderBy('lastInteractionAt', 'desc')
      .get()

    return snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }))
  }

  /**
   * Get chat list query with pagination
   * Migrated from: serviceCollaborations.getChatList
   */
  getChatList(type: string, lastInteractionAt: Date | Timestamp | null): Query {
    const threeDaysAgo = Timestamp.fromDate(
      new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
    )

    let q = db
      .collection('collaborations')
      .where('lastInteractionAt', '>=', threeDaysAgo)
      .where('status', '==', type === ChatType.mychat ? 'handled' : 'queue')

    q = q.orderBy('lastInteractionAt', 'desc')

    if (lastInteractionAt) {
      q = q.startAfter(lastInteractionAt)
    }

    q = q.limit(25)

    return q as unknown as Query
  }

  /**
   * Find collaboration by ID (returns query)
   * Migrated from: serviceCollaborations.findByCollaborationId
   */
  findById(collaborationId: string): Query {
    return db
      .collection('collaborations')
      .where('__name__', '==', collaborationId) as unknown as Query
  }

  // ============================================================================
  // MUTATION METHODS
  // ============================================================================

  /**
   * Update collaboration status and assign to user
   * Migrated from: serviceCollaborations.updateStatus
   */
  async updateStatus(
    id: string,
    status: string,
    userId: string,
    messageFailed: string
  ): Promise<string | null> {
    removeFromNotificationsByCollabID(id)
    const userRef = createRefConn('users', userId)

    try {
      let arrUser = [userRef]
      const getCollabByid = await this.getById(id)

      if (getCollabByid) {
        // toUserData is set by getById() enrichment
        const toUserData = (getCollabByid as any).toUserData
        toUserData?.forEach((item: any) => {
          const isExist = arrUser?.some((user) => user?.id === item?.id)
          if (!isExist && arrUser?.length > 0) {
            let refId = createRefConn('users', item.id)
            arrUser.push(refId)
          }
        })
      }

      const roomsSnapshot = await db
        .collection('collaborations')
        .doc(id)
        .collection('rooms')
        .get()

      if (!roomsSnapshot.empty) {
        roomsSnapshot.docs.map(async (x) => {
          const roomDoc = x
          await roomDoc.ref.update({
            status: status,
            updatedAt: new Date(),
            toUser: arrUser,
          })
        })
      } else {
        return null
      }

      await db.collection('collaborations').doc(id).update({
        status: status,
        updatedAt: new Date(),
        handleAt: new Date(),
        handledByUser: userRef,
        activeHandler: userRef,
        toUser: arrUser,
      })

      if (!roomsSnapshot.empty) {
        roomsSnapshot.docs.map(async (x) => {
          await db
            .collection('collaborations')
            .doc(id)
            .collection('rooms')
            .doc(x.id)
            .update({
              status: status,
              updatedAt: new Date(),
              toUser: arrUser,
              profileName: getCollabByid?.profileName,
            })
        })
      }

      return id
    } catch (error) {
      console.error('Error updating collaboration status:', error)
      return null
    }
  }

  /**
   * Batch update collaboration statuses
   * Migrated from: serviceCollaborations.updateListCollabStatus
   */
  async updateBatchStatus(
    CIQIds: Array<string | number>,
    status: string,
    userId: string,
    messageFailed: string,
    callback: (successIds: string[]) => void
  ): Promise<void> {
    let totalFailed = 0
    let successCollab: string[] = []
    const userRef = createRefConn('users', userId)

    for (let index = 0; index < CIQIds.length; index++) {
      const id = CIQIds[index]
      if (id === undefined || id === null) continue

      const resultCollab = await createRefConn('collaborations', id.toString())
        .get()
        .then(async (collab) => {
          let respCollab = collab.data() as Collaboration
          respCollab.id = collab.id
          return respCollab
        })

      let arrUser: any[] = []
      if (
        resultCollab &&
        resultCollab.toUser &&
        Array.isArray(resultCollab.toUser) &&
        resultCollab.toUser.length > 0
      ) {
        totalFailed += 1
        continue
      }

      arrUser.push(userRef)

      const success: boolean = await db
        .collection('collaborations')
        .doc(id.toString())
        .update({
          status: status,
          updatedAt: new Date(),
          handleAt: new Date(),
          toUser: arrUser,
        })
        .then(() => {
          successCollab.push(id.toString())
          return true
        })
        .catch((error) => {
          console.error('Error updateBatchStatus:', error)
          return false
        })

      if (!success) {
        totalFailed += 1
      }
      removeFromNotificationsByCollabID(id.toString())
    }

    if (totalFailed > 0) {
      alert(totalFailed + ' ' + messageFailed)
    }
    callback(successCollab)
  }

  /**
   * Update last interaction metadata
   * Migrated from: serviceCollaborations.updateLastInteraction
   */
  async updateLastInteraction(
    collaborationId: string,
    newMessage: Message
  ): Promise<void> {
    let lastMessage = newMessage.textContent
    if (
      (newMessage.textContent === '' || newMessage.textContent === undefined) &&
      newMessage.filename !== undefined
    ) {
      lastMessage = newMessage.filename
    }

    await db.collection('collaborations').doc(collaborationId).update({
      lastInteractionChannel: newMessage.channel,
      lastInteractionAt: newMessage.createdAt,
      lastInteractionType: newMessage.messageType,
      lastInteractionMessage: lastMessage,
    })
  }

  /**
   * Set collaboration status to handled
   * Migrated from: serviceCollaborations.setStatusToHandled
   */
  async setStatusToHandled(id: string, handledUserId: string): Promise<void> {
    const handleUserRef = createRef('users', handledUserId)

    await db
      .collection('collaborations')
      .doc(id)
      .update({
        status: 'handled',
        handledByUser: handleUserRef,
        activeHandler: handleUserRef,
        toUser: arrayUnion(handleUserRef),
        updatedAt: Timestamp.now(),
        handleAt: Timestamp.now(),
        lastInteractionAt: Timestamp.now(),
      })
  }

  /**
   * Create a new friend collaboration
   * Migrated from: serviceCollaborations.create
   */
  async create(uid: string, friendId: string): Promise<string | undefined> {
    const userRef = createRefConn('users', uid)
    const friendRef = createRefConn('users', friendId)

    try {
      const ref = await db.collection('collaborations').add({
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true,
        joinedUsers: [userRef, friendRef],
        lastInteractionAt: new Date(),
        lastInteractionType: '',
        lastInteractionMessage: '',
        lastInteractionChannel: '',
      })
      return ref.id
    } catch (error) {
      console.error('Error creating collaboration:', error)
      return undefined
    }
  }

  /**
   * Delete a collaboration
   * Migrated from: serviceCollaborations.deleteCollaboration
   */
  async delete(id: string): Promise<void> {
    try {
      await db.collection('collaborations').doc(id).delete()
    } catch (error) {
      console.error('Error deleting collaboration:', error)
    }
  }
}

export default CollaborationRepositoryImpl.getInstance()
